import { ReparoApi } from '@api';
import { DataTable } from '@components/ui/Table';
import type { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useRef, useState } from 'react';

type ChannelSummary = {
    channelId: string;
    productCount: number;
    periodCount: number;
};

type ChannelMeta = {
    id: string;
    label: string;
};

type UploadForecastModalProps = {
    open: boolean;
    onClose: () => void;
    channels: ChannelMeta[];
    onUploaded?: (info: { processed: number; channel: string }) => void;
};

type RowState = {
    id: string;
    label: string;
    fileName?: string;
    status?: 'idle' | 'uploading' | 'success' | 'error';
    errorMessage?: string;
};

const UploadForecastModal: React.FC<UploadForecastModalProps> = ({
    open,
    onClose,
    channels,
    onUploaded,
}) => {
    const [rows, setRows] = useState<RowState[]>([]);
    const [channelSummaries, setChannelSummaries] = useState<Record<string, ChannelSummary>>({});
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    // channels list se table rows banana
    useEffect(() => {
        if (!open) return;
        setRows(
            channels.map((c) => ({
                id: c.id,
                label: c.label,
                status: 'idle',
            })),
        );
        setChannelSummaries({});
        ReparoApi<{ channels: ChannelSummary[] }>({
            method: 'GET',
            url: '/forecasts/channel-summary',
        })
            .then((res) => {
                const map: Record<string, ChannelSummary> = {};
                for (const item of res.channels) {
                    map[item.channelId] = item;
                }
                setChannelSummaries(map);
            })
            .catch(() => {
                // silently ignore — "No file uploaded yet" remains as fallback
            });
    }, [channels, open]);

    // helper to update ek row
    const setRow = (id: string, patch: Partial<RowState>) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, ...patch } : r)),
        );
    };

    const handleChooseFile = (id: string) => {
        const input = inputRefs.current[id];
        if (input) {
            input.value = '';
            input.click();
        }
    };

    const handleFileSelected = async (
        id: string,
        label: string,
        file?: File | null,
    ) => {
        if (!file) return;

        setRow(id, {
            fileName: file.name,
            status: 'uploading',
            errorMessage: undefined,
        });

        try {
            const formData = new FormData();
            formData.append('file', file);
            // curl me: channel=Shopify -> yahan label bhej rahe
            formData.append('channel', label);

            const response = await ReparoApi({
                method: 'POST',
                url: '/forecasts/upload',
                body: formData,
            }) as { processed?: number } | undefined;

            setRow(id, { status: 'success' });

            if (onUploaded) {
                onUploaded({ processed: response?.processed ?? 0, channel: label });
            }
        } catch (err: any) {
            setRow(id, {
                status: 'error',
                errorMessage:
                    err?.error?.message ||
                    err?.message ||
                    'Upload failed. Please try again.',
            });
        }
    };

    if (!open) return null;

    /* --------------------------- DataTable columns --------------------------- */

    const columns: ColumnDef<RowState>[] = [
        {
            accessorKey: 'label',
            header: '',
            cell: ({ row }) => {
                const r = row.original;
                const status = r.status ?? 'idle';
                const summary = channelSummaries[r.id];
                return (
                    <div className="flex flex-col">
                        <span className="text-xs font-medium text-slate-900">
                            {r.label}
                        </span>
                        {status === 'idle' && !r.fileName && !summary && (
                            <span className="text-[11px] text-slate-400">
                                No file uploaded yet
                            </span>
                        )}
                        {status === 'idle' && !r.fileName && summary && (
                            <span className="text-[11px] text-slate-500">
                                {summary.productCount} {summary.productCount === 1 ? 'product' : 'products'} · {summary.periodCount} {summary.periodCount === 1 ? 'period' : 'periods'}
                            </span>
                        )}
                        {r.fileName && (
                            <span className="text-[11px] text-slate-500">
                                {r.fileName}
                            </span>
                        )}
                        {status === 'uploading' && (
                            <span className="text-[11px] text-slate-500">
                                Uploading…
                            </span>
                        )}
                        {status === 'success' && (
                            <span className="text-[11px] text-emerald-600">
                                Uploaded successfully
                            </span>
                        )}
                        {status === 'error' && r.errorMessage && (
                            <span className="text-[11px] text-red-600">
                                {r.errorMessage}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'action',
            header: '',
            cell: ({ row }) => {
                const r = row.original;
                return (
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => handleChooseFile(r.id)}
                            className="rounded-full border border-slate-200 bg-slate-900 px-3 py-1 text-[11px] font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                            disabled={r.status === 'uploading'}
                        >
                            {r.fileName ? 'Replace' : 'Upload'}
                        </button>

                        {/* hidden file input – row wise */}
                        <input
                            ref={(el) => {
                                inputRefs.current[r.id] = el;
                            }}
                            type="file"
                            accept=".csv"
                            className="hidden"
                            onChange={(e) =>
                                handleFileSelected(
                                    r.id,
                                    r.label,
                                    e.target.files?.[0],
                                )
                            }
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div
                className="relative w-full max-w-4xl rounded-2xl bg-white p-6 shadow-2xl"
                style={{ height: '800px', overflow: 'scroll' }}
            >
                {/* Close button */}
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-500 hover:bg-slate-200"
                >
                    ✕
                </button>

                <h2 className="mb-2 text-lg font-semibold text-slate-900">
                    Edit Demand Forecasts
                </h2>
                <p className="mb-4 text-xs text-slate-500">
                    Upload channel-wise CSV files to update your demand
                    forecasts.
                </p>

                {/* CSV instructions */}
                <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 text-xs font-medium text-slate-700">
                        CSV Format Instructions
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-[480px] border-separate border-spacing-0 text-[11px] text-slate-700">
                            <thead>
                                <tr>
                                    <th className="border border-slate-200 bg-white px-3 py-2 text-left">
                                        SKU Code
                                    </th>
                                    <th className="border border-slate-200 bg-white px-3 py-2 text-left">
                                        Country
                                    </th>
                                    <th className="border border-slate-200 bg-white px-3 py-2 text-left">
                                        Jan-26
                                    </th>
                                    <th className="border border-slate-200 bg-white px-3 py-2 text-left">
                                        Feb-26
                                    </th>
                                    <th className="border border-slate-200 bg-white px-3 py-2 text-left">
                                        Mar-26
                                    </th>
                                    <th className="border border-slate-200 bg-white px-3 py-2 text-left">
                                        …
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-slate-200 bg-white px-3 py-2">
                                        SKU-001
                                    </td>
                                    <td className="border border-slate-200 bg-white px-3 py-2">
                                        India
                                    </td>
                                    <td className="border border-slate-200 bg-white px-3 py-2">
                                        450
                                    </td>
                                    <td className="border border-slate-200 bg-white px-3 py-2">
                                        480
                                    </td>
                                    <td className="border border-slate-200 bg-white px-3 py-2">
                                        500
                                    </td>
                                    <td className="border border-slate-200 bg-white px-3 py-2">
                                        …
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-2 text-[11px] text-slate-500">
                        Columns: SKU Code, Country, then monthly forecasts in MMM-YY format (e.g. Jan-26, Feb-26).
                        Each cell value is the total forecasted quantity for that period.
                    </p>
                </div>

                {/* Channel uploads – using DataTable */}
                <div
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                    style={{ height: '450px', overflow: 'scroll' }}
                >
                    <div className="mb-3 text-xs font-medium text-slate-700">
                        Channels
                    </div>

                    <DataTable<RowState>
                        data={rows}
                        columns={columns}
                        enableGlobalSearch={false}
                        enablePagination={false}
                        emptyState="No channels configured yet."
                        classes={{
                            container:
                                'border-0 bg-transparent shadow-none px-0 py-0',
                            toolbar: 'hidden',
                            thead: 'hidden',
                            tableWrapper: 'p-0',
                            bodyCell: 'align-middle',
                            footer: 'hidden',
                        }}
                    />
                </div>

                {/* Footer buttons */}
                <div className="mt-4 flex justify-end gap-2 text-xs">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-slate-700 hover:bg-slate-50"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadForecastModal;
